import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuListFilter } from 'react-icons/lu';
import Button from '../ui/Button';
import GlassIcon from '../ui/GlassIcon';
import Modal from '../ui/Modal';
import { GLASS_FIELD } from '../ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';

const selectClass = `${GLASS_FIELD} tw-cursor-pointer tw-appearance-none tw-pr-9 [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100`;

const FilterField = ({ field, value, state, onChange }) => {
  const { t } = useTranslation();
  const id = `data-filter-${field.key}`;
  const current = value === undefined || value === null ? '' : String(value);

  return (
    <div className="tw-space-y-1.5">
      <label htmlFor={id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-1 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
        <span className="tw-truncate">{field.label}</span>
        {state === 'pending' && <span className="tw-shrink-0 tw-font-medium tw-text-amber-600 dark:tw-text-amber-300">{t('dataTable.search.minChars')}</span>}
        {state === 'applied' && <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-teal-500" />}
      </label>
      {field.type === 'select' ? (
        <span className="tw-relative tw-block">
          <select id={id} value={current} onChange={(event) => onChange(event.target.value)} className={selectClass}>
            <option value="">{t('dataTable.filter.all')}</option>
            {field.options.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>{option.label}</option>
            ))}
          </select>
          <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
        </span>
      ) : (
        <input
          id={id}
          type={field.type === 'date' ? 'date' : 'text'}
          value={current}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.type === 'date' ? undefined : t('dataTable.filter.placeholder')}
          enterKeyHint="done"
          className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
        />
      )}
    </div>
  );
};

FilterField.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['text', 'select', 'date']).isRequired,
    options: PropTypes.array,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  state: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

/**
 * Column filters where the table headers aren't shown (the card list on
 * narrow screens). A bottom sheet on phones; results update as you type.
 * `fields`: [{ key, label, type, options }].
 */
const FilterSheet = ({ isOpen, onClose, fields, filters, getFilterState, onFilterChange, onClearAll, resultCount }) => {
  const { t, language } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('dataTable.filters')}
      subtitle={t('dataTable.filterSheet.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuListFilter aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClearAll}>{t('dataTable.clearAll')}</Button>
          <Button variant="primary" onClick={onClose} className="max-sm:tw-flex-1">
            {t('dataTable.filterSheet.show', { count: resultCount })}
          </Button>
        </>
      )}
    >
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        {fields.map((field) => (
          <FilterField
            key={field.key}
            field={field}
            value={filters[field.key]}
            state={getFilterState(field.key, filters[field.key])}
            onChange={(value) => onFilterChange(field.key, value)}
          />
        ))}
      </div>
    </Modal>
  );
};

FilterSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  filters: PropTypes.object.isRequired,
  getFilterState: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
};

export default FilterSheet;
